/**
 * When this browser last agreed a sync with the instance — §8's `lastSyncedEpochSec`, on its own key.
 *
 * It used to live *inside* `visious.settings`, justified by a state that no longer exists: the
 * comment there described it as `settingsSyncState`'s `undecided`, the thing that held a push back
 * until the reader had answered "use instance settings or keep local". That prompt was replaced on
 * 2026-08-01 ([settings.md](../../../../docs/specs/settings.md)) and `settingsSyncState` now reads
 * `{ signedIn, pulled }` and nothing else. So the stamp branches nothing: `settingsSync.ts` puts it
 * in its snapshot and the settings page prints it as "last synced ‹when›".
 *
 * Moving it out is structural rather than cosmetic. A signed-in boot stamps this on every load, and
 * while it shared the settings key that write reached every other tab as a settings change — which
 * the store now absorbs by comparing identity, but only for as long as the stamp stays out of
 * `VisiousSettings`. Nothing enforced that; a key of its own does.
 *
 * Its own module for `theme.ts`'s reason: one storage key, no imports, so both the sync module that
 * owns the value and the settings store that has to clear it can reach it without a cycle.
 */

/** Exported so `settingsSync.ts` can watch for another tab writing it. */
export const SYNC_STAMP_KEY = 'visious.lastSynced';

/** The stamp, or `null` for a browser that has never agreed a sync — or has just signed out. */
export function readSyncStamp(): number | null {
  let raw: string | null;
  try {
    raw = globalThis.localStorage.getItem(SYNC_STAMP_KEY);
  } catch {
    // A blocked storage context is a browser that cannot remember agreeing, which is what `null` says.
    return null;
  }
  if (raw === null) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/**
 * Record that this browser and the instance now agree — or, with `null`, that they do not, which is
 * what signing out and clearing the settings both mean.
 *
 * It does not publish anything. Every caller in `settingsSync.ts` emits its own snapshot immediately
 * after, and the settings store no longer has any reason to hear about this at all.
 */
export function writeSyncStamp(epochSec: number | null): void {
  try {
    if (epochSec === null) globalThis.localStorage.removeItem(SYNC_STAMP_KEY);
    else globalThis.localStorage.setItem(SYNC_STAMP_KEY, String(epochSec));
  } catch {
    // Same as above: a stamp that cannot be stored is a browser that will ask again next boot.
  }
}
